import { ActivatedRoute, Router } from '@angular/router'
import { Component, OnInit } from '@angular/core'
import { ConfirmationService, MessageService } from 'primeng/api'
import { Subject, debounceTime } from 'rxjs'

import { DocAnchor, ALL_FIELDS, BlockDef, FIELD_SCHEMA, FieldDef, FieldGroup, FieldHelp } from '../../data/constants/field-schema'
import { ApiAlgorithmConfigService, IEkatenaWallet } from '../../data/services/api/api-algorithm-config.service'
import { IAlgorithmConfig, IAlgorithmDefaults, IAlgorithmParams, IParamRange, IPreviewProfile, ISimulationResult } from '../../data/interfaces/algorithm-config.interface'
import { STATUS_LABEL, STATUS_SEVERITY, DRAFT_KEY_PREFIX, DRAFT_TTL_DAYS, IAlgorithmConfigDraft } from '../../data/constants/algorithm-config.constants'

@Component({
  selector: 'app-algorithm-config-detail',
  templateUrl: './algorithm-config-detail.component.html',
  styleUrls: ['./algorithm-config-detail.component.scss'],
  providers: [MessageService, ConfirmationService],
})
export class AlgorithmConfigDetailComponent implements OnInit {
  config: IAlgorithmConfig | null = null
  params: IAlgorithmParams | null = null
  defaults: IAlgorithmDefaults | null = null
  /** Los parámetros de la versión de la que se partió. Alimenta el indicador de «modificado». */
  baseParams: IAlgorithmParams | null = null
  baseVersion: number | null = null

  esNueva = false
  cargando = true
  errorCarga: string | null = null
  guardando = false

  nombre = ''
  descripcion = ''
  motivo = ''

  // Ayuda
  ayudaActiva: { titulo: string; help: FieldHelp; docAnchor?: DocAnchor } | null = null

  //* Slug del manual de negocio al que apunta la ayuda. Vive aquí y no en el esquema para que
  //* renombrar el documento sea un cambio de una línea, no de veinte anclas.
  readonly manualSlug = 'manual-syntage'
  mostrarComoFunciona = false

  // Borrador local
  /** Lo que se encontró guardado al entrar. Mientras no sea null, la pantalla muestra el aviso. */
  borradorPendiente: IAlgorithmConfigDraft | null = null
  //* Un `Subject` propio y no `previewTrigger`: la vista previa pega al servidor y el nombre o el
  //* motivo no tienen por qué provocar una petición cada vez que se teclea una letra.
  private readonly cambios = new Subject<void>()

  // Vista previa
  preview: IPreviewProfile[] = []
  previewCargando = false
  private readonly previewTrigger = new Subject<void>()

  // Créditos del proveedor de respaldo
  /**
   * El saldo de créditos de Ekatena. Se pide una vez al entrar para que un Admin no tenga que abrir
   * el panel del proveedor solo para saber si le queda con qué consultar.
   *
   * ! Se llama `creditos` y no `cartera` a propósito: en esta pantalla «cartera» ya significa **la
   * ! cartera de arrendamientos** —«Simular sobre cartera real», «Impacto sobre la cartera real»— y
   * ! usar la misma palabra para dos cosas distintas la vuelve ambigua justo donde más importa. El
   * ! tipo conserva el nombre inglés `Wallet` porque es como lo llama la API de Ekatena.
   */
  creditos: IEkatenaWallet | null = null
  creditosCargando = false

  // Simulación
  simulando = false
  simulacion: ISimulationResult | null = null
  mostrarSimulacion = false
  tamanoMuestra = 50

  readonly schema = FIELD_SCHEMA

  constructor(
    private readonly api: ApiAlgorithmConfigService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly messageService: MessageService,
    private readonly confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    //* La vista previa se recalcula al editar, pero con freno: cada tecla dispararía una petición.
    this.previewTrigger.pipe(debounceTime(450)).subscribe(() => this.cargarPreview())
    this.cambios.pipe(debounceTime(800)).subscribe(() => this.guardarBorrador())

    const id = this.route.snapshot.paramMap.get('id')
    this.esNueva = !id || id === 'new'

    this.api.getDefaults().subscribe((r) => {
      if (!r.error) this.defaults = r.data
      if (this.esNueva) this.iniciarDesdeActiva()
      else this.cargar(id!)
    })
    this.cargarCreditos()
  }

  //* Una versión nueva arranca clonando la ACTIVA, no los valores de fábrica: lo que casi siempre se
  //* quiere es «lo que hay hoy, con un ajuste», no volver al origen.
  private iniciarDesdeActiva(): void {
    this.api.getActive().subscribe((r) => {
      this.cargando = false
      if (r.error) {
        this.errorCarga = r.msg
        return
      }
      const base = r.data
      this.params = this.clonar(base?.params ?? this.defaults?.params ?? null)
      this.baseParams = this.clonar(base?.params ?? this.defaults?.params ?? null)
      this.baseVersion = base?.version ?? null
      this.config = base ? ({ ...base, status: 'draft', version: base.version + 1, derivedFromVersion: base.version } as IAlgorithmConfig) : null
      this.detectarBorrador()
      this.cargarPreview()
    })
  }

  private cargar(id: string): void {
    this.api.getById(id).subscribe((r) => {
      this.cargando = false
      if (r.error) {
        this.errorCarga = r.msg
        return
      }
      this.config = r.data
      this.params = this.clonar(r.data!.params)
      this.nombre = r.data!.name
      this.descripcion = r.data!.description
      this.motivo = r.data!.changeReason
      this.detectarBorrador()
      this.cargarPreview()

      //* Para el indicador de «modificado» hace falta la versión de la que salió esta.
      if (r.data!.derivedFromVersion) {
        this.api.getAll().subscribe((lista) => {
          if (lista.error) return
          const padre = (lista.data || []).find((c) => c.version === r.data!.derivedFromVersion)
          this.baseParams = padre ? this.clonar(padre.params) : null
          this.baseVersion = padre?.version ?? null
        })
      }
    })
  }

  private clonar<T>(v: T): T {
    return JSON.parse(JSON.stringify(v ?? null))
  }

  // ── Estado ──────────────────────────────────────────────────────────────────

  /** Solo los borradores se editan: activas y archivadas son historia que las verificaciones citan. */
  get editable(): boolean {
    return this.esNueva || this.config?.status === 'draft'
  }

  get readonly(): boolean {
    return !this.editable
  }

  etiquetaEstado(status: string): string {
    return STATUS_LABEL[status as keyof typeof STATUS_LABEL] ?? status
  }

  severidadEstado(status: string): string {
    return STATUS_SEVERITY[status as keyof typeof STATUS_SEVERITY] ?? 'info'
  }

  // ── Valores ─────────────────────────────────────────────────────────────────

  //* Los interruptores viven en el mismo árbol de parámetros, así que se leen y escriben aparte
  //* de los numéricos: un booleano nunca está «vacío» y no bloquea el guardado.
  interruptor(path: string): boolean {
    return path.split('.').reduce((o: any, k) => o?.[k], this.params as any) === true
  }

  setInterruptor(path: string, v: boolean): void {
    const partes = path.split('.')
    const ultimo = partes.pop()!
    const destino = partes.reduce((o: any, k) => o?.[k], this.params as any)
    if (destino) destino[ultimo] = v
    this.previewTrigger.next()
    this.cambios.next()
  }

  valor(path: string): number | null {
    const v = path.split('.').reduce((o: any, k) => o?.[k], this.params as any)
    return typeof v === 'number' ? v : null
  }

  setValor(path: string, v: number | null): void {
    const partes = path.split('.')
    const ultimo = partes.pop()!
    const destino = partes.reduce((o: any, k) => o?.[k], this.params as any)
    if (destino) destino[ultimo] = v
    this.previewTrigger.next()
    this.cambios.next()
  }

  onTiersChange(): void {
    this.previewTrigger.next()
    this.cambios.next()
  }

  rango(path: string): IParamRange | null {
    return this.defaults?.ranges?.[path] ?? null
  }

  /**
   * Un campo vacío NO desaparece del formulario: queda marcado y bloquea el guardado.
   *
   * ! Antes el formulario derivaba sus campos de los valores (`typeof v === 'number'`), así que al
   * ! borrar la cifra el campo se filtraba fuera del DOM y no había manera de recuperarlo. Ahora la
   * ! estructura viene del esquema estático y el valor vacío es solo un estado inválido más.
   */
  vacio(path: string): boolean {
    return this.valor(path) === null
  }

  get camposVacios(): FieldDef[] {
    if (!this.params) return []
    //* Los interruptores quedan fuera: un booleano no puede estar vacío.
    return ALL_FIELDS.filter((f) => f.kind !== 'switch' && this.vacio(f.path))
  }

  /** Devuelve el campo al valor de la versión de la que se partió. */
  restaurar(f: FieldDef): void {
    const previo = f.path.split('.').reduce((o: any, k) => o?.[k], (this.baseParams ?? this.defaults?.params) as any)
    if (typeof previo === 'number') {
      this.setValor(f.path, previo)
      this.messageService.add({ severity: 'info', summary: 'Valor restaurado', detail: `${f.label}: ${previo}`, life: 2500 })
    }
  }

  /** Marca los campos que difieren de la versión base, para que el cambio sea visible sin buscarlo. */
  modificado(path: string): boolean {
    if (!this.baseParams) return false
    const previo = path.split('.').reduce((o: any, k) => o?.[k], this.baseParams as any)
    if (typeof previo === 'boolean') return previo !== this.interruptor(path)
    return typeof previo === 'number' && previo !== this.valor(path)
  }

  get totalModificados(): number {
    if (!this.baseParams || !this.params) return 0
    return ALL_FIELDS.filter((f) => this.modificado(f.path)).length
  }

  tiersDe(path: string): any[] {
    return path.split('.').reduce((o: any, k) => o?.[k], this.params as any) ?? []
  }

  setTiers(path: string, v: any[]): void {
    const partes = path.split('.')
    const ultimo = partes.pop()!
    const destino = partes.reduce((o: any, k) => o?.[k], this.params as any)
    if (destino) destino[ultimo] = v
    this.previewTrigger.next()
    this.cambios.next()
  }

  // ── Ayuda ───────────────────────────────────────────────────────────────────

  //* Un solo overlay reutilizado en vez de treinta instancias: se puebla al abrirlo.
  abrirAyuda(event: Event, titulo: string, help: FieldHelp, overlay: any, docAnchor?: DocAnchor): void {
    this.ayudaActiva = { titulo, help, docAnchor }
    overlay.toggle(event)
  }

  abrirAyudaBloque(event: Event, b: BlockDef, overlay: any): void {
    this.ayudaActiva = { titulo: b.label, help: { what: b.hint, where: b.help, example: '', effect: '' }, docAnchor: b.docAnchor }
    overlay.toggle(event)
  }

  /**
   * Cierra el overlay antes de navegar al manual.
   *
   * Sin esto el `p-overlayPanel` sobrevive al cambio de ruta y se queda flotando sobre la pantalla
   * de documentación, anclado a un botón que ya no está en el DOM.
   */
  irAlManual(overlay: any): void {
    overlay.hide()
  }

  // ── Vista previa ────────────────────────────────────────────────────────────

  private cargarPreview(): void {
    if (!this.params || this.camposVacios.length) return
    this.previewCargando = true
    this.api.preview(this.params).subscribe((r) => {
      this.previewCargando = false
      //* Un error aquí no interrumpe: la vista previa es una ayuda, no un requisito para editar.
      if (!r.error) this.preview = r.data || []
    })
  }

  // ── Acciones ────────────────────────────────────────────────────────────────

  private bloqueadoPorVacios(): boolean {
    const vacios = this.camposVacios
    if (!vacios.length) return false
    this.messageService.add({
      severity: 'warn',
      summary: vacios.length === 1 ? 'Hay un campo vacío' : `Hay ${vacios.length} campos vacíos`,
      detail: `${vacios.map((f) => f.label).join(', ')}. Cada uno tiene un botón para restaurar su valor anterior.`,
      life: 10000,
    })
    return true
  }

  // ── Borrador local ───────────────────────────────────────────────────────────
  //* La pantalla guarda sola lo que llevas escrito para que irte a consultar el manual —o pulsar
  //* «Volver» sin querer— no cueste treinta campos. Deliberadamente NO hay `CanDeactivate`: la
  //* gracia es poder navegar sin fricción, no que la app te retenga.

  private get llaveBorrador(): string {
    return `${DRAFT_KEY_PREFIX}:${this.esNueva ? 'new' : this.config?._id ?? 'new'}`
  }

  /** Marca los campos de texto, que van con `[(ngModel)]` y no pasan por los setters. */
  marcarCambio(): void {
    this.cambios.next()
  }

  private guardarBorrador(): void {
    if (!this.editable || !this.params) return
    try {
      const draft: IAlgorithmConfigDraft = {
        params: this.params,
        nombre: this.nombre,
        descripcion: this.descripcion,
        motivo: this.motivo,
        paramsHash: this.config?.paramsHash ?? null,
        savedAt: new Date().toISOString(),
      }
      localStorage.setItem(this.llaveBorrador, JSON.stringify(draft))
    } catch {
      //* Cuota llena o modo privado. Perder el borrador es molesto; impedir editar, peor.
    }
  }

  /**
   * Busca un borrador al entrar. **No lo aplica**: solo levanta el aviso.
   *
   * Restaurar en silencio enseñaría números distintos de los que tiene el servidor sin que el
   * usuario lo note, y estos números fijan precios. Que lo decida él.
   */
  private detectarBorrador(): void {
    if (!this.editable) return
    let draft: IAlgorithmConfigDraft | null = null
    try {
      const crudo = localStorage.getItem(this.llaveBorrador)
      draft = crudo ? (JSON.parse(crudo) as IAlgorithmConfigDraft) : null
    } catch {
      draft = null
    }
    if (!draft?.params) return this.olvidarBorrador()

    const dias = (Date.now() - new Date(draft.savedAt).getTime()) / 86400000
    if (!(dias >= 0) || dias > DRAFT_TTL_DAYS) return this.olvidarBorrador()

    //* Si la versión del servidor cambió desde que se guardó, el borrador parte de otra cosa.
    //* Aplicarlo mezclaría dos configuraciones distintas sin que se note.
    if (draft.paramsHash && this.config?.paramsHash && draft.paramsHash !== this.config.paramsHash) {
      this.olvidarBorrador()
      this.messageService.add({
        severity: 'info',
        summary: 'Se descartó un borrador',
        detail: 'Tenías cambios sin guardar, pero esta versión se modificó desde entonces y ya no partían de lo mismo.',
        life: 8000,
      })
      return
    }

    this.borradorPendiente = draft
  }

  restaurarBorrador(): void {
    const draft = this.borradorPendiente
    if (!draft) return
    this.params = this.clonar(draft.params)
    this.nombre = draft.nombre
    this.descripcion = draft.descripcion
    this.motivo = draft.motivo
    this.borradorPendiente = null
    this.cargarPreview()
    this.messageService.add({ severity: 'success', summary: 'Borrador restaurado', detail: 'Seguimos donde lo dejaste.', life: 4000 })
  }

  descartarBorrador(): void {
    this.olvidarBorrador()
    this.borradorPendiente = null
  }

  private olvidarBorrador(): void {
    try {
      localStorage.removeItem(this.llaveBorrador)
    } catch {
      /* nada que hacer */
    }
  }

  /** Cuánto hace que se guardó, para que el aviso diga algo útil y no una fecha ISO. */
  antiguedadBorrador(): string {
    if (!this.borradorPendiente) return ''
    const min = Math.floor((Date.now() - new Date(this.borradorPendiente.savedAt).getTime()) / 60000)
    if (min < 1) return 'hace un momento'
    if (min < 60) return `hace ${min} min`
    const horas = Math.floor(min / 60)
    if (horas < 24) return `hace ${horas} h`
    const dias = Math.floor(horas / 24)
    return dias === 1 ? 'ayer' : `hace ${dias} días`
  }

  guardar(): void {
    if (!this.nombre?.trim()) {
      this.messageService.add({ severity: 'warn', summary: 'Falta el nombre', detail: 'Ponle un nombre reconocible a la versión.' })
      return
    }
    if (!this.motivo?.trim()) {
      this.messageService.add({ severity: 'warn', summary: 'Falta el motivo', detail: 'Explica por qué se crea esta versión: sin motivo no es auditable.' })
      return
    }
    if (this.bloqueadoPorVacios()) return

    this.guardando = true
    const body = { name: this.nombre.trim(), description: this.descripcion, changeReason: this.motivo.trim(), params: this.params! }
    const req = this.esNueva ? this.api.create(body) : this.api.update(this.config!._id, body)

    req.subscribe((r) => {
      this.guardando = false
      if (r.error) {
        this.mostrarErrorDeValidacion(r)
        return
      }
      //* El borrador local ya cumplió: lo guardado vive en el servidor. Se limpia ANTES de navegar
      //* porque después la llave cambia (de `new` al id nuevo) y quedaría huérfano para siempre.
      this.olvidarBorrador()
      this.messageService.add({ severity: 'success', summary: 'Borrador guardado', detail: `Versión ${r.data!.version}` })
      this.router.navigate(['/algorithm-config', r.data!._id])
    })
  }

  //* La validación del servidor devuelve la lista de problemas concretos. Mostrarlos todos —y no un
  //* «configuración inválida»— es la diferencia entre corregir en un intento o adivinar.
  private mostrarErrorDeValidacion(r: { msg: string; errors?: string[] }): void {
    if (r.errors?.length) {
      for (const e of r.errors) this.messageService.add({ severity: 'error', summary: 'Revisa la configuración', detail: e, life: 12000 })
      return
    }
    this.messageService.add({ severity: 'error', summary: 'No se pudo guardar', detail: r.msg, life: 8000 })
  }

  simular(): void {
    if (this.bloqueadoPorVacios()) return
    this.simulando = true
    this.mostrarSimulacion = true
    //* Se simula con los parámetros del formulario, guardados o no.
    this.api.simulate({ params: this.params!, sampleSize: this.tamanoMuestra }).subscribe((r) => {
      this.simulando = false
      if (r.error) {
        this.mostrarSimulacion = false
        this.mostrarErrorDeValidacion(r as any)
        return
      }
      this.simulacion = r.data
    })
  }

  activar(): void {
    if (!this.config?._id) return
    const esRollback = this.config.status === 'archived'
    this.confirmationService.confirm({
      header: esRollback ? `Volver a la versión ${this.config.version}` : `Activar la versión ${this.config.version}`,
      message: esRollback
        ? 'Se vuelve a estos valores tal cual estaban. Es la forma de revertir rápido si la configuración vigente resultó mala. Las verificaciones ya emitidas no cambian.'
        : 'A partir de ahora todas las ofertas nuevas se calcularán con esta configuración. Las verificaciones ya emitidas no cambian.',
      icon: 'fa-solid fa-triangle-exclamation',
      acceptLabel: esRollback ? 'Volver a esta versión' : 'Activar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-success',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => {
        this.api.activate(this.config!._id).subscribe((r) => {
          if (r.error) {
            this.messageService.add({ severity: 'error', summary: 'No se pudo activar', detail: r.msg, life: 8000 })
            return
          }
          this.messageService.add({ severity: 'success', summary: 'Versión activada', detail: 'Las ofertas nuevas ya usan esta configuración.' })
          this.cargar(this.config!._id)
        })
      },
    })
  }

  duplicar(): void {
    this.router.navigate(['/algorithm-config/new'])
  }

  volver(): void {
    this.router.navigate(['/algorithm-config'])
  }

  reintentar(): void {
    this.errorCarga = null
    this.cargando = true
    this.ngOnInit()
  }

  /**
   * Resuelve la fórmula de un grupo sustituyendo `{ruta.del.campo}` por su valor actual.
   *
   * Siete campos que solo tienen sentido juntos —la normalización del score— se leen como números
   * sueltos hasta que se ve la fórmula armada. Al editarlos, la fórmula se actualiza sola y el
   * efecto deja de ser abstracto.
   */
  formulaResuelta(f: string): string {
    return (f || '').replace(/\{([^}]+)\}/g, (_m, path) => {
      const v = this.valor(path)
      return v === null ? '—' : String(v)
    })
  }

  /**
   * Convierte el énfasis `**así**` de los textos de ayuda en `<strong>`.
   *
   * Se usa con `[innerHTML]`, y es seguro porque **la fuente es una constante del propio código**
   * (`field-schema.ts`), no entrada de usuario ni contenido de la base. Si algún día estos textos
   * llegaran de una API o de un campo editable, esto tendría que pasar por el sanitizador o
   * eliminarse: `innerHTML` con contenido ajeno es XSS.
   */
  negritas(texto: string): string {
    return (texto || '').replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
  }

  claseDelta(v: number): string {
    return v > 0 ? 'up' : v < 0 ? 'down' : 'flat'
  }

  /**
   * El color para el VALOR nuevo, no para su Δ. Es igual salvo en el empate: `flat` es gris, y
   * teñir de gris los 47 renglones que no se movieron dejaba la tabla entera con pinta de
   * deshabilitada. Sin cambio, el valor se queda en su color normal y solo destacan los que sí
   * se movieron — que es lo único que se está buscando en esta pantalla.
   */
  claseValor(v: number): string {
    return v > 0 ? 'up' : v < 0 ? 'down' : ''
  }

  // ── Créditos del proveedor de respaldo ────────────────────────────────────────────

  /**
   * Pide el saldo. **No bloquea nada** ni propaga el error a la pantalla: si el proveedor no
   * responde, la tarjeta dice que no se pudo leer y el resto de la configuración sigue editable.
   */
  cargarCreditos(refrescar = false): void {
    this.creditosCargando = true
    this.api.getEkatenaWallet(refrescar).subscribe((r) => {
      this.creditosCargando = false
      this.creditos = r.data
      if (refrescar && !r.error && r.data?.available) {
        this.messageService.add({ severity: 'success', summary: 'Saldo actualizado', detail: `Quedan ${r.data.balance} créditos de Ekatena.` })
      }
    })
  }

  /**
   * Cuántas consultas alcanza el saldo.
   *
   * La cuenta se hace **aquí y no en el servidor** a propósito: usa el costo por reporte que el Admin
   * tiene en el formulario en este momento, así que se mueve mientras edita. Un número calculado
   * atrás quedaría congelado en lo que el servidor supuso ([[ADR-0002]]: el front prefiere primitivos).
   */
  get consultasQueAlcanzan(): number | null {
    const saldo = this.creditos?.balance
    const costo = Number(this.valor('providers.wallet.reportCost'))
    if (!this.creditos?.available || saldo === null || saldo === undefined || !(costo > 0)) return null
    return Math.floor(saldo / costo)
  }

  /**
   * Cuántas de esas consultas puede gastar el respaldo automático antes de topar con la reserva.
   *
   * Es el número que de verdad importa y el que nadie tiene a mano: el saldo dice cuánto hay, no
   * cuánto va a dejar disponible la política que se está editando.
   */
  get consultasAntesDeLaReserva(): number | null {
    const saldo = this.creditos?.balance
    const costo = Number(this.valor('providers.wallet.reportCost'))
    const reserva = Number(this.valor('providers.wallet.minBalance'))
    if (!this.creditos?.available || saldo === null || saldo === undefined || !(costo > 0)) return null
    return Math.max(0, Math.floor((saldo - Math.max(0, reserva || 0)) / costo))
  }

  /** La reserva ya está por encima del saldo: el respaldo automático no se dispararía ni una vez. */
  get reservaBloqueaElRespaldo(): boolean {
    return this.creditos?.available === true && this.consultasAntesDeLaReserva === 0
  }

  /** Por qué no hay saldo que enseñar, dicho para quien no conoce la integración. */
  get creditosNoDisponibles(): string | null {
    if (!this.creditos || this.creditos.available) return null
    switch (this.creditos.reason) {
      case 'not_configured':
        return 'Este ambiente no tiene configuradas las credenciales de Ekatena, así que no hay saldo que consultar.'
      case 'unsupported_environment':
        return 'El proveedor respondió sin saldo. Es lo normal en el ambiente de pruebas, donde las consultas no se cobran — en producción sí llega el número.'
      default:
        return 'No se pudo consultar el saldo en este momento. No significa que no haya créditos: significa que no se sabe cuántos.'
    }
  }
}
